/**
 * Human-readable audit log labels.
 * Keys match `public.audit_log_action` / category / target_type / source.
 * Unknown tokens fall back to spaced words so the UI never shows raw backend enums.
 */

export const AUDIT_LOG_ACTIONS = [
  'login',
  'logout',
  'login_failed',
  'password_reset_requested',
  'password_changed',
  'mfa_enabled',
  'mfa_disabled',
  'patient_viewed',
  'patient_list_viewed',
  'cpr_revealed',
  'appointment_viewed',
  'invoice_viewed',
  'journal_viewed',
  'document_viewed',
  'document_downloaded',
  'patient_created',
  'patient_updated',
  'patient_archived',
  'patient_restored',
  'appointment_created',
  'appointment_updated',
  'appointment_cancelled',
  'appointment_completed',
  'invoice_created',
  'invoice_updated',
  'invoice_sent',
  'invoice_paid',
  'journal_created',
  'journal_updated',
  'journal_deleted',
  'document_uploaded',
  'document_deleted',
  'patient_data_exported',
  'invoice_data_exported',
  'appointment_data_exported',
  'bulk_export',
  'clinic_settings_changed',
  'user_invited',
  'user_removed',
  'permission_changed',
  'unauthorized_access_attempt',
  'suspicious_activity',
  'data_breach_detected',
  'dental_shortcut_created',
  'dental_shortcut_updated',
  'dental_shortcut_deleted',
  'treatment_plan_created',
  'treatment_plan_updated',
  'treatment_plan_completed',
  'treatment_plan_cancelled',
  'tooth_image_uploaded',
  'tooth_image_deleted',
  'perio_exam_recorded',
  'perio_measurement_updated',
  'dental_data_archived',
  'dental_session_locked',
  'dental_session_unlocked',
  'tooth_status_created',
  'tooth_status_updated',
  'tooth_treatment_created',
  'tooth_treatment_updated',
  'pay_tenant_provisioned',
  'tenant_api_key_rotated',
  'pay_callback_secret_rotated',
  'forms_tenant_provisioned',
  'forms_assignment_completed',
  'forms_assignment_expired',
  'forms_respondent_deleted',
  'payment_received',
  'payment_failed',
  'payment_refunded',
  'payment_disputed',
  'pay_account_restricted',
] as const;

export type AuditLogActionName = (typeof AUDIT_LOG_ACTIONS)[number];

const TYPE_KEY_ALIASES: Record<string, string> = {
  journal_entry: 'journal',
};

type Translate = (key: string, options?: { defaultValue: string }) => string;

export function humanizeAuditToken(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) return '';
  return trimmed
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .replace(/^./, (char) => char.toUpperCase());
}

function labeled(t: Translate, kind: string, value: string): string {
  if (!value) return '';
  const keyToken = TYPE_KEY_ALIASES[value] ?? value;
  return t(`audit.${kind}_${keyToken}`, { defaultValue: humanizeAuditToken(value) });
}

export function auditActionLabel(t: Translate, action: string): string {
  return labeled(t, 'action', action);
}

export function auditCategoryLabel(t: Translate, category: string): string {
  return labeled(t, 'category', category);
}

export function auditTargetTypeLabel(t: Translate, targetType: string): string {
  return labeled(t, 'type', targetType);
}

export function auditSourceLabel(t: Translate, source: string): string {
  return labeled(t, 'source', source);
}

const UUID_LIKE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const PATIENT_UUID_IDENTIFIER =
  /^patient\s+[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function isTechnicalAuditIdentifier(value: string | null | undefined): boolean {
  if (!value) return true;
  const trimmed = value.trim();
  return UUID_LIKE.test(trimmed) || PATIENT_UUID_IDENTIFIER.test(trimmed);
}

const PURPOSE_ALIASES: Record<string, string> = {
  'manual reveal from patient detail page': 'patient_detail',
  patient_detail: 'patient_detail',
  'Vist i tandmodul patient-strip': 'dental_strip',
  dental_strip: 'dental_strip',
  sd_invoice_submission: 'sd_invoice_submission',
  sd_invoice_validation: 'sd_invoice_validation',
  sd_credit_note_submission: 'sd_credit_note_submission',
};

export function auditPurposeLabel(t: Translate, purpose: string | null | undefined): string {
  if (!purpose) return '';
  const trimmed = purpose.trim();
  const alias = PURPOSE_ALIASES[trimmed] ?? trimmed;
  return labeled(t, 'purpose', alias);
}

export function auditActorLabel(
  t: Translate,
  actorName?: string | null,
  actorEmail?: string | null,
): string {
  const name = actorName?.trim();
  if (name) return name;
  const email = actorEmail?.trim();
  if (email && email.toLowerCase() !== 'system') return email;
  return t('audit.actor_system', { defaultValue: 'System' });
}

export function auditTargetSummary(
  t: Translate,
  targetType: string | null | undefined,
  targetIdentifier: string | null | undefined,
): string | null {
  if (!targetType && !targetIdentifier) return null;
  const typeLabel = targetType ? auditTargetTypeLabel(t, targetType) : '';
  if (!targetIdentifier || isTechnicalAuditIdentifier(targetIdentifier)) {
    return typeLabel || null;
  }
  return typeLabel ? `${typeLabel}: ${targetIdentifier}` : targetIdentifier;
}
